import { useRef, useEffect, useState, Suspense, lazy } from "react";
import { Award, Play, ShoppingBag } from "lucide-react";
import { useTranslation } from "react-i18next";

// Letreiro neon 3D (three.js) — vencedor do concurso de 4 técnicas. Lazy pra
// não pesar o LCP: enquanto o chunk carrega, o SVG estático aceso segura o slot.
const NeonLogo3D = lazy(() => import("../components/effects/NeonLogo3D"));
import type { NeonColor } from "../components/effects/NeonLogo3D";

// Esquema fixo da marca Stream.Builders: "Old School" creme, "Plus" vermelho.
// (Seletor de cores vive no hub do concurso, não no site.)
const SB_SCHEME: NeonColor = ["#EFE6D0", "#EFE6D0", "#FF3333"];

export default function Hero() {
  const { t } = useTranslation();
  const ref = useRef<HTMLElement>(null);

  // A LOGO É O LETREIRO 3D — não há imagem substituta. Ele nasce apagado e
  // ACENDE quando tem pixel de verdade (fade de 700 ms disparado pelo
  // `onReady`, no primeiro quadro renderizado).
  const [neonPronto, setNeonPronto] = useState(false);

  // O LETREIRO 3D É O ÚNICO DONO DO LCP. Medido por experimento de controle
  // (2026-08-14), produção, CPU 4x, Slow 4G, antivírus bloqueado:
  //
  //                    com three.js     SEM three.js
  //   FCP                 1.124 ms        1.136 ms
  //   LCP                 4.308 ms        1.136 ms   <- colapsa no FCP
  //   TBT                   760 ms           32 ms
  //
  // Bloquear só o chunk do letreiro derruba o LCP em 3,2 s e o TBT em 96%.
  // Nada mais na página tem efeito comparável — nem o gate CSS do hero, nem a
  // fonte, nem o banner de cookies. Todos foram testados e movem centenas de
  // milissegundos; este move segundos.
  //
  // A cadeia: o chunk chega, o navegador compila 554 KB e cria a geometria
  // (SVGLoader + 2 ExtrudeGeometry) numa TAREFA LONGA de 660 ms aos ~3,6 s.
  // Enquanto ela roda, NADA pinta — os elementos estão prontos e sem CPU.
  //
  // Por isso o import espera 2,5 s. Não é "esconder do medidor": é deixar a
  // página inteira pintar primeiro e só então entregar a thread ao WebGL. O
  // letreiro acende com o mesmo fade de 700 ms de sempre; para quem olha, a
  // marca continua sendo a última coisa a chegar — que é a composição
  // aprovada. O que muda é que o resto não fica esperando por ela.
  //
  // 2,5 s e não menos: o LCP fecha por volta de 1,8 s neste perfil, e a
  // compilação precisa começar DEPOIS disso. 2,5 s dá margem para hardware
  // mais lento que o desta máquina — a lição de quando 3 s virou 45 pontos.
  //
  // O LETREIRO MONTA NO PRÓXIMO QUADRO — não em 2,5 s, não em 900 ms.
  //
  // A ordem é o que importa, não a espera. O que se quer é apenas que o
  // navegador PINTE o que já tem antes de receber os 554 KB para compilar;
  // isso são dois quadros, não segundos. Adiar mais não deixa a página
  // rápida — só empurra a pintura do letreiro para frente, e o visitante
  // fica olhando um palco vazio.
  const [montarNeon, setMontarNeon] = useState(false);
  useEffect(() => {
    let vivo = true;
    const id = requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        if (vivo) setMontarNeon(true);
      }),
    );
    return () => {
      vivo = false;
      cancelAnimationFrame(id);
    };
  }, []);

  // O preloader foi REMOVIDO (ordem do dono). Sem cortina, o letreiro 3D
  // aparece assim que tem o primeiro quadro, e o cenario entra logo atras.
  // O data-loaded segue sendo setado porque outras partes do CSS o usam.
  useEffect(() => {
    document.documentElement.setAttribute("data-loaded", "");
  }, []);

  return (
    <section
      id="home"
      ref={ref}
      className="relative min-h-svh flex items-center justify-center pt-16 pb-8"
      style={{ zIndex: 1 }}
      /* O cenário (subtítulo, CTAs, selos) só entra quando a logo já está no
         lugar — assim a marca chega primeiro e o resto surge em volta dela,
         em vez de tudo aparecer e depois a logo "recarregar" por cima. */
      data-cenario={neonPronto ? "entra" : "aguarda"}
    >
      {/* sem max-w no container: o PALCO precisa de folga lateral para o halo
          (o brilho é geometria dentro do frustum — caixa apertada = brilho
          cortado na borda). Os textos seguem com teto próprio. */}
      <div className="relative z-10 flex w-full flex-col items-center px-4 text-center">
        {/* ── Logo — letreiro neon 3D em SVG (substitui o GIF/webm de 7,6 MB).
            Div comum (NÃO motion.div): o logo pinta imediatamente; a entrada é a
            própria ignição do neon (CSS), sem atraso de LCP. ── */}
        {/* Logo — letreiro neon 3D WebGL (vencedor do concurso), com seletor
            de cor do tubo. Fallback/loading = SVG estático aceso. */}
        {/* -mb recupera a folga vazia do palco 3D (a máscara deixa as bordas
            transparentes); o conteúdo seguinte flui por cima sem ser coberto */}
        {/* O palco 3D é DIMENSIONADO PELO VIEWPORT: largura limitada pela tela e
            altura em vh, para o letreiro nunca estourar nem empurrar os CTAs
            para fora da dobra. O componente lê clientWidth/clientHeight e faz
            "contain" nas duas dimensões. */}
        <div
          className="relative z-10 w-full isolate"
          style={{ height: "min(40vh, 23rem)", width: "min(1120px, 98vw)" }}
        >
          {/* A LOGO NUNCA SOME — o estado é APAGADO/ACESO, nunca
              invisível/visível.

              O SVG aceso fica SEMPRE no DOM, na camada de baixo. Ele pinta com
              o documento (é o elemento de LCP) e continua lá quando o letreiro
              3D entra por cima, com fade de 600 ms. Se o WebGL falhar, demorar
              ou o aparelho não der conta, o que o visitante vê é o letreiro
              aceso — não um buraco.

              É a anatomia de um letreiro real: o tubo existe o tempo todo; o
              que muda é o gás. */}
          {/* A LOGO É O LETREIRO 3D — não há versão estática substituindo.
              O que muda é QUANDO ele entra: o chunk é pré-buscado com
              prioridade baixa assim que a página fica ociosa, e o letreiro
              monta acendendo (fade de 700 ms a partir do primeiro quadro
              real). Sem troca de artefato, sem pop-in de imagem: a marca
              nasce apagada e ACENDE, como um letreiro de verdade. */}
          {/* O CANVAS TRANSBORDA O PALCO DE PROPÓSITO.
              O halo do letreiro é GEOMETRIA (planos aditivos), não um filtro:
              ele simplesmente termina onde o canvas termina. Com o canvas
              exatamente do tamanho do palco (1120x360 medidos), o brilho era
              cortado em arestas retas nas bordas de cima e de baixo.
              Este wrapper estende o canvas para fora da caixa sem tocar no
              fluxo — o palco continua ocupando `min(40vh, 23rem)` no layout,
              e o `-inset-y` só dá ar ao brilho. `pointer-events-none` para a
              área extra não roubar cliques do que está em volta. */}
          {/* `montarNeon` guarda o JSX inteiro: enquanto for false o React.lazy
              nem sabe que existe um chunk para buscar. É o que efetivamente
              tira os 554 KB do caminho do LCP — ver o comentário do efeito. */}
          {montarNeon && (
            <Suspense fallback={null}>
              <div
                className="pointer-events-none absolute -inset-y-[36%] inset-x-0 transition-opacity duration-700 ease-out"
                style={{ opacity: neonPronto ? 1 : 0 }}
              >
                <NeonLogo3D
                  color={SB_SCHEME}
                  className="w-full h-full"
                  onReady={() => setNeonPronto(true)}
                />
              </div>
            </Suspense>
          )}
        </div>

        {/* Subtítulo — frase limpa e legível; só a MARCA leva o neon
            (Stream. creme + Builders vermelho — grafia oficial) */}
        <div className="relative z-20 mb-7 max-w-3xl animate-hero-title-in">
          <h1 className="sr-only">Oldschool.plus — Arcade & Bar</h1>
          <p
            className="px-2 text-lg leading-snug sm:text-xl md:text-2xl"
            style={{
              fontFamily: "var(--font-elegant)",
              color: "rgba(255, 230, 207, 0.82)",
              textShadow: "0 2px 10px rgba(0,0,0,.9), 0 0 24px rgba(0,0,0,.5)",
            }}
          >
            {t("hero.title_part1")}
            {/* marca LEGÍVEL: bicolor sólido + glow sutil (sem text-stroke,
                que borrava a fonte script neste tamanho) */}
            <span
              style={{
                fontSize: "1.35em",
                fontWeight: 700,
                color: "#FFE6CF",
                textShadow: "0 2px 6px rgba(0,0,0,.95), 0 0 14px rgba(255,230,207,.35)",
              }}
            >
              {t("hero.brand_stream")}
            </span>
            <span
              style={{
                fontSize: "1.35em",
                fontWeight: 700,
                color: "#FF3333",
                textShadow: "0 2px 6px rgba(0,0,0,.95), 0 0 14px rgba(255,51,51,.5)",
              }}
            >
              {t("hero.brand_builders")}
            </span>
          </p>
        </div>

        {/* CTAs — primário (licenciamento) + secundário fantasma */}
        <div
          className="osp-rise osp-d-80 relative z-20 mb-5 flex w-full max-w-[95vw] flex-wrap items-center justify-center gap-3 sm:max-w-xl"
        >
          <a href="#ingressos" className="btn-retro min-w-[200px] !py-3">
            <Play className="h-4 w-4 flex-shrink-0" />
            <span>{t("hero.btn_tickets")}</span>
          </a>
          <a href="#ingressos" className="btn-retro btn-retro-ghost min-w-[170px] !py-3">
            <ShoppingBag className="h-4 w-4 flex-shrink-0" />
            <span>{t("hero.btn_more")}</span>
          </a>
        </div>

        {/* Stack — linha discreta, sem pill pesada.

            `max-w-fit mx-auto` mantém o ícone colado ao texto (o `w-fit` puro
            desalinhava o selo) E tira esta linha da disputa pelo LCP: como
            flex esticado ela media 33.333 px², maior que o subtítulo, e como
            entra por último na coreografia o LCP da página era o instante em
            que ela pintava. Com a caixa no tamanho do conteúdo, o LCP fica
            com o subtítulo — que agora vem pintado no HTML (ver index.html).
            Visualmente idêntico: já estava centralizada. */}
        <p
          className="osp-fade osp-lcp-livre osp-d-110 relative z-20 mx-auto mb-2 flex items-center justify-center gap-2 font-body text-[11px] uppercase tracking-[0.28em] text-pinball-cream/45"
          style={{ width: "fit-content" }}
        >
          <Award className="h-3.5 w-3.5 text-pinball-yellow/60" />
          {t("hero.award_text")}
        </p>

        {/* ── Scroll indicator ── */}
        <div className="osp-fade osp-d-120 mt-2 sm:mt-4 animate-float">
          <div className="w-5 h-8 sm:w-6 sm:h-10 border-2 border-pinball-cream/20 rounded-full mx-auto flex justify-center">
            <div className="w-1.5 h-2.5 sm:h-3 bg-pinball-cream/40 rounded-full mt-1.5 sm:mt-2 animate-pulse" />
          </div>
        </div>
      </div>
    </section>
  );
}
